import { useMemo, useState } from 'react';
import { toSvgDataUri } from '../utils/svg';

interface LogoPreviewProps {
  svgContent: string;
  cropX: number;
  cropY: number;
  zoom: number;
  size: number;
}

/**
 * Rendu carré recadré/zoomé d'un logo, utilisé à la fois dans la grille de
 * la bibliothèque et dans l'éditeur (voir LogoEditorModal). cropX/cropY/zoom
 * sont des fractions indépendantes de la taille d'affichage : recalculer
 * l'échelle de base à partir de la taille naturelle de l'image à chaque
 * rendu garantit un rendu identique quelle que soit la taille de la boîte.
 *
 * La taille naturelle n'est connue qu'après le chargement de l'image
 * (onLoad), mais la transformation elle-même est recalculée à CHAQUE rendu
 * à partir de cette taille mémorisée — pas seulement au chargement — sinon
 * l'aperçu resterait figé quand on glisse ou qu'on change le zoom.
 *
 * Rendu exclusivement via <img src="data:..."> : un SVG hostile ne peut pas
 * exécuter de script dans ce contexte, contrairement à un <svg> inline dans
 * le DOM ou à <object>/<iframe>.
 */
export function LogoPreview({ svgContent, cropX, cropY, zoom, size }: LogoPreviewProps) {
  const [naturalSize, setNaturalSize] = useState<{ width: number; height: number } | null>(null);

  const handleLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    setNaturalSize({ width: img.naturalWidth || size, height: img.naturalHeight || size });
  };

  const transform = useMemo(() => {
    if (!naturalSize) return null;
    const baseScale = Math.min(size / naturalSize.width, size / naturalSize.height);
    const scale = baseScale * zoom;
    const width = naturalSize.width * scale;
    const height = naturalSize.height * scale;
    const centeredLeft = (size - width) / 2;
    const centeredTop = (size - height) / 2;
    return {
      width,
      height,
      left: centeredLeft - cropX * size,
      top: centeredTop - cropY * size,
    };
  }, [naturalSize, cropX, cropY, zoom, size]);

  return (
    <div
      style={{
        width: size,
        height: size,
        overflow: 'hidden',
        position: 'relative',
        background: 'repeating-conic-gradient(#f0f0f0 0% 25%, #ffffff 0% 50%) 50% / 12px 12px',
        borderRadius: 6,
      }}
    >
      <img
        src={toSvgDataUri(svgContent)}
        onLoad={handleLoad}
        alt=""
        draggable={false}
        style={
          transform
            ? {
                position: 'absolute',
                width: transform.width,
                height: transform.height,
                left: transform.left,
                top: transform.top,
                userSelect: 'none',
              }
            : { opacity: 0 }
        }
      />
    </div>
  );
}
